import './agenda-template-health-professional-apply-modal.scss';

import { useMutation } from '@apollo/client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { useAppContext } from '../../../../data/context/app-context';
import { useAgendaTemplateFrequencyValues } from '../../../../data/data/agenda/select-values';
import { useHealthProfessionalItems } from '../../../../data/data/health-professionals/use-health-professional-items';
import { useTemplateItem } from '../../../../data/data/templates/use-template-item';
import { useTemplateRangeCheck } from '../../../../data/data/templates/use-template-range-check';
import {
  AgendaAppointmentItemDataFragment,
  ApplyAgendaTemplateItemDocument,
  ConsultType,
  GetAgendaItemsDocument,
  TemplateFrequency,
} from '../../../../data/types/api/graphql';
import { formatDate } from '../../../../data/utils/date.utils';
import { useFormatters } from '../../../../data/utils/formatters.utils';
import { useNavigateWithHealthProvider } from '../../../../data/utils/router.utils';
import { AlertMessageType } from '../../../../elements/alert-message/alert-message';
import { AppointmentExceptionList } from '../../../../elements/appointment-exception-list/appointment-exception-list';
import {
  Button,
  ButtonIcon,
  ButtonType,
} from '../../../../elements/button/button';
import { InputCheckboxController } from '../../../../elements/form/controllers/input-checkbox';
import { InputFieldController } from '../../../../elements/form/controllers/input-field';
import { InputSelectController } from '../../../../elements/form/controllers/input-select';
import { FormColumns, FormRow } from '../../../../elements/form/form';
import { BaseInputIcon } from '../../../../elements/form/input/base-input';
import { CustomFormattedMessage } from '../../../../elements/intl/custom-formatted-message';
import { Modal, ModalWidth } from '../../../../elements/modal/modal';
import { ModalPrompt } from '../../../../elements/modal/modal-prompt';
import { TemplateEntryList } from '../../../../elements/template-entry-list/template-entry-list';
import { InputSelectTemplateFieldController } from '../../../form/controllers/input-select-template-field-controller';
import { useAgendaTemplateHealthProfessionalApplyForm } from './form/use-agenda-template-health-professional-apply-modal';

type AgendaTemplateHealthProfessionalApplyModalProps = {
  healthProviderId: string | null | undefined;
  healthProfessionalId?: string | null | undefined;
  onSaved?: () => void;
  onCancel?: () => void;
  onClose?: () => void;
};

export function AgendaTemplateHealthProfessionalApplyModal({
  healthProviderId,
  healthProfessionalId,
  onSaved,
  onCancel,
  onClose,
}: AgendaTemplateHealthProfessionalApplyModalProps) {
  const intl = useIntl();

  const { showAlertMessage } = useAppContext();
  const { getFullName } = useFormatters();

  const {
    data: healthProfessionalItemsData,
    loading: healthProfessionalItemsLoading,
  } = useHealthProfessionalItems({
    healthProviderId,
    onlyAgenda: true,
  });

  const healthProfessionalItems = useMemo(
    () => healthProfessionalItemsData?.items ?? [],
    [healthProfessionalItemsData]
  );

  const [formStep, setFormStep] = useState<number>(1);

  const onHandleFormStep = useCallback((nextStep: number) => {
    setFormStep(nextStep);
  }, []);

  const { form, formIsValid } = useAgendaTemplateHealthProfessionalApplyForm();

  useEffect(() => {
    if (healthProfessionalId) {
      form.reset({
        ...form.getValues(),
        healthProfessionalId,
      });
    }
  }, [form, healthProfessionalId]);

  const { data: templateFrequencyData } = useAgendaTemplateFrequencyValues();

  const templateId = form.watch('templateId');

  const selectedHealthProfessionalId = form.watch('healthProfessionalId');

  const { data: templateItemData } = useTemplateItem(templateId);

  const frequency = form.watch('frequency');

  const isMultiple = useMemo(
    () => frequency !== TemplateFrequency.Once,
    [frequency]
  );

  useEffect(() => {
    if (frequency === TemplateFrequency.Once) {
      form.setValue('endDate', '', {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [form, frequency]);

  const startDate = form.watch('startDate');
  const endDate = form.watch('endDate');
  const overwrite = form.watch('overwrite');

  const isOverwrite = useMemo(() => {
    if (overwrite && typeof overwrite === 'boolean') {
      return overwrite;
    }
    return false;
  }, [overwrite]);

  const {
    data: templateRangeCheckData,
    loading: templateRangeCheckLoading,
    error: templateRangeCheckError,
  } = useTemplateRangeCheck({
    healthProviderId,
    healthProfessionalId,
    templateId,
    startDate,
    endDate,
    frequency,
    overWriteConsultTypeElse: false, // always return all the appointments
  });

  // reset overwrite
  useEffect(() => {
    if (!templateRangeCheckData.hasOverwritableItems && isOverwrite === true) {
      form.setValue('overwrite', false);
    }
  }, [form, isOverwrite, templateRangeCheckData.hasOverwritableItems]);

  const [applyTemplateItem, { loading: applyTemplateItemLoading }] =
    useMutation(ApplyAgendaTemplateItemDocument, {
      refetchQueries: [GetAgendaItemsDocument],
    });

  const onHandleSubmit = useCallback(() => {
    form.handleSubmit(async () => {
      const values = form.getValues();

      try {
        const result = await applyTemplateItem({
          variables: {
            ...values,
            healthProviderId,
            startDate: values.startDate,
            endDate:
              values.frequency !== TemplateFrequency.Once
                ? values.endDate
                : null,
            overWriteConsultTypeElse: isOverwrite,
          },
        });

        if (result.data?.applyTemplate.agendaTemplate?.success) {
          showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: intl.formatMessage({
              id: 'agenda.template-apply.success',
            }),
          });

          onSaved?.();

          onClose?.();
        } else {
          throw new Error(
            'An unknown error has occurred in the server respond.'
          );
        }
      } catch (error) {
        showAlertMessage({
          type: AlertMessageType.ERROR,
          message: (error as Error).message,
        });
      }
    })();
  }, [
    form,
    applyTemplateItem,
    healthProviderId,
    isOverwrite,
    showAlertMessage,
    intl,
    onSaved,
    onClose,
  ]);

  const [cancelPromptOpen, setCancelPromptOpen] = useState<boolean>(false);

  const onHandleCancel = useCallback(
    (confirmed: boolean = false) => {
      if (!confirmed && form.formState.isDirty) {
        setCancelPromptOpen(true);
      } else {
        onCancel?.();

        onClose?.();
      }
    },
    [form.formState.isDirty, onCancel, onClose]
  );

  const navigate = useNavigateWithHealthProvider();

  const onHandleAppointmentException = useCallback(
    (appointment: AgendaAppointmentItemDataFragment) => {
      onCancel?.();

      onClose?.();

      navigate(
        `/agenda/${appointment.healthProfessional?.id}/${formatDate(
          appointment.startTime
        )}`
      );
    },
    [navigate, onCancel, onClose]
  );

  return (
    <>
      <Modal
        testId="apply-template"
        loading={healthProfessionalItemsLoading}
        title={<CustomFormattedMessage id="agenda.template-apply.title" />}
        width={ModalWidth.LARGE}
        onClose={onHandleCancel}
        buttons={
          formStep === 2 ? (
            <Button inverted onClick={() => setFormStep(1)}>
              <CustomFormattedMessage id="common.back" />
            </Button>
          ) : (
            <>
              <Button
                type={ButtonType.SECONDARY}
                inverted
                onClick={() => onHandleCancel(false)}
              >
                <CustomFormattedMessage id="common.cancel" />
              </Button>

              <Button
                icon={
                  applyTemplateItemLoading || templateRangeCheckLoading
                    ? ButtonIcon.LOADING
                    : ButtonIcon.NONE
                }
                disabled={
                  !formIsValid ||
                  !templateId ||
                  applyTemplateItemLoading ||
                  templateRangeCheckLoading ||
                  templateRangeCheckError !== undefined ||
                  (templateRangeCheckData.hasAppointments && !isOverwrite)
                }
                onClick={onHandleSubmit}
              >
                <CustomFormattedMessage id="agenda.template-apply.save" />
              </Button>
            </>
          )
        }
      >
        <FormProvider {...form}>
          {formStep === 1 && (
            <>
              <FormRow>
                <InputSelectController<string>
                  name="healthProfessionalId"
                  label={
                    <>
                      <CustomFormattedMessage id="common.health-professional" />
                      <small>*</small>
                    </>
                  }
                  placeholder={intl.formatMessage({
                    id: 'common.health-professional-placeholder',
                  })}
                  disabled={healthProfessionalItemsLoading}
                  options={healthProfessionalItems.map(option => ({
                    ...option,
                    key: option.id,
                    title: getFullName(option),
                  }))}
                  value={selectedHealthProfessionalId}
                />
              </FormRow>
              <FormColumns className="agenda-template-columns">
                <FormRow>
                  <InputSelectTemplateFieldController
                    healthProviderId={healthProviderId}
                    healthProfessionalId={selectedHealthProfessionalId}
                    name="templateId"
                    disabled={healthProfessionalItemsLoading}
                    placeholder={intl.formatMessage({
                      id: 'common.templates-select-placeholder',
                    })}
                    label={
                      <>
                        <CustomFormattedMessage id="common.template" />
                        <small>*</small>
                      </>
                    }
                    value={templateItemData?.id}
                  />
                </FormRow>

                <FormRow>
                  <Button
                    inverted
                    disabled={!templateItemData}
                    onClick={() => onHandleFormStep(2)}
                  >
                    <CustomFormattedMessage id="common.example" />
                  </Button>
                </FormRow>
              </FormColumns>

              <FormRow>
                <InputSelectController<TemplateFrequency>
                  name="frequency"
                  label={
                    <>
                      <CustomFormattedMessage id="agenda.template-apply.frequency" />
                      <small>*</small>
                    </>
                  }
                  options={templateFrequencyData}
                />
              </FormRow>

              <FormColumns>
                <FormRow>
                  <InputFieldController
                    icon={BaseInputIcon.DATE}
                    type="date"
                    name="startDate"
                    label={
                      <>
                        <CustomFormattedMessage id="common.start-date" />
                        <small>*</small>
                      </>
                    }
                    min={formatDate(new Date())}
                  />
                </FormRow>

                <FormRow>
                  <InputFieldController
                    icon={BaseInputIcon.DATE}
                    type="date"
                    name="endDate"
                    label={
                      <>
                        <CustomFormattedMessage id="common.end-date" />
                        {isMultiple && <small>*</small>}
                      </>
                    }
                    min={formatDate(new Date())}
                    disabled={!isMultiple}
                  />
                </FormRow>
              </FormColumns>

              <FormRow>
                <InputCheckboxController<boolean>
                  name="overwrite"
                  disabled={!templateRangeCheckData.hasOverwritableItems}
                  label={
                    <CustomFormattedMessage id="agenda.template-apply.overwrite" />
                  }
                  options={[
                    {
                      key: true,
                      title: (
                        <CustomFormattedMessage id="agenda.template-apply.overwrite-placeholder" />
                      ),
                    },
                  ]}
                />
              </FormRow>

              <FormRow>
                <h4>
                  <CustomFormattedMessage
                    id="common.agenda-appointment.appointments-blocking"
                    values={{
                      count: isOverwrite
                        ? templateRangeCheckData.appointments.filter(
                            x => x.consultType !== ConsultType.Else
                          ).length
                        : templateRangeCheckData.appointments.length,
                    }}
                  />
                </h4>

                <AppointmentExceptionList
                  items={
                    isOverwrite
                      ? templateRangeCheckData.appointments.filter(
                          x => x.consultType !== ConsultType.Else
                        )
                      : templateRangeCheckData.appointments
                  }
                  includePatient
                  onClick={onHandleAppointmentException}
                />
              </FormRow>
            </>
          )}

          {formStep === 2 && (
            <>
              <h3>{templateItemData?.name}</h3>

              <h4>
                <CustomFormattedMessage id="template.template-details" />
              </h4>
              <TemplateEntryList items={templateItemData?.entries ?? []} />
            </>
          )}
        </FormProvider>
      </Modal>

      {cancelPromptOpen && (
        <ModalPrompt
          onConfirm={() => onHandleCancel(true)}
          onClose={() => setCancelPromptOpen(false)}
        >
          <p>
            <CustomFormattedMessage id="common.confirm-not-saved-changes" />
          </p>
        </ModalPrompt>
      )}
    </>
  );
}
